import { defineStore } from "pinia";
import axios from "axios";
import { ShiftInterface } from "../pages/interfaces/shiftInterface";
import Http from "@/services/Http";

type ClientUser = {
    id: string | null;
    client_id: string | null;
    timezone: string | null;
    timezone_friendly_name?: string | null;
    permission: string;
    positions: string[];
    approval_enabled: boolean;
    first_name?: string | null;
    last_name?: string | null;
};

type Timezone = {
    name: string;
};

export type MyClient = {
    name: string;
    timezone: Timezone;
    country_id: number;
};
export type MyClientAccountManager = {
    legal_business_name?: string;
    status?: string;
    hasAssignedAccountManager?: boolean;
    timezone: Timezone;
    country_id: number;
};

export const useAuthStore = defineStore("AuthStore", {
    state: (): {
        role: string;
        invalidLogin: boolean;
        authUser: {
            id: number;
            employee_id?: number;
            employee_birthdate?: string;
            uuid: string;
            name: string;
            email: string;
            two_factor: string;
            two_factor_secret: string;
            is_auto_password: boolean;
            is_employee: boolean;
        };
        activatedPermissionsName: any[];
        qrSvg: string;
        token: string;
        permissions: any[];
        myTeam: any[];
        isLoggedIn: boolean;
        invalid2FA: boolean;
        impersonator: string;
        user: any[];
        is2FAenabled: boolean;
        impersonated: string;
        email: string;
        myClient: any[];
        myClientAccountManager: MyClientAccountManager | null;
        myShift: ShiftInterface;
        showGlobalModal: boolean;
        globalModalTitle: string;
        globalModalMessage: string;
        lateNotification: boolean;
        clientUser: ClientUser;
        canApproveTicket: boolean;
        loginLinks: Record<string, string>;
    } => ({
        user: [],
        isLoggedIn: false,
        invalidLogin: false,
        token: "",
        email: "",
        authUser: {
            id: 0,
            employee_id: 0,
            uuid: "",
            name: "",
            email: "",
            two_factor: "",
            two_factor_secret: "",
            is_auto_password: false,
            is_employee: false
        },
        role: "",
        permissions: [],
        activatedPermissionsName: [],
        myTeam: [],
        myClient: [],
        myClientAccountManager: null,
        myShift: {
            id: 0,
            name: "",
            timezone_id: "",
            shift_active_start_date: "",
            start_time: "",
            end_time: "",
            total_hours: 0,
            shift_breaks: [],
            shift_overtimes: [],
            used_on_holidays: false,
            used_on_restdays: false,
            work_days: []
        },
        is2FAenabled: false,
        invalid2FA: false,
        qrSvg: "",
        impersonated: "",
        impersonator: "",
        showGlobalModal: false,
        globalModalTitle: "",
        globalModalMessage: "",
        lateNotification: false,
        clientUser: {
            id: null,
            timezone: null,
            client_id: null,
            permission: "",
            positions: [],
            approval_enabled: false,
            first_name: null,
            last_name: null
        },
        canApproveTicket: false,
        loginLinks: {}
    }),
    actions: {
        async login(email: string, password: string) {
            try {
                await axios.get(
                    import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
                );
                const response = await axios.post(
                    import.meta.env.VITE_APP_URL + "/login",
                    {
                        email: email,
                        password: password
                    }
                );
                if (response?.status === 200) {
                    console.log("login response");
                    if (response.data.two_factor === true) {
                        return { status: "two_factor_required" };
                    } else {
                        return { status: "success", data: response.data };
                    }
                }
            } catch (error) {
                console.error(error);
                return { status: "error", error: error };
            }
        },
        validate2FA(code: any) {
            axios
                .get(import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie")
                .then(response => {
                    axios
                        .post(
                            import.meta.env.VITE_APP_URL +
                                "/two-factor-challenge",
                            {
                                code: code
                            }
                        )
                        .then(response => {
                            console.log("response");
                            console.log(response);
                            if (response.status === 204) {
                                this.isLoggedIn = true;
                                this.invalidLogin = false;
                                this.generateToken();
                            }
                        })
                        .catch(error => {
                            console.log("two error");
                            this.invalid2FA = true;
                        });
                });
        },
        generateToken() {
            console.log("generate token");
            let options = {
                method: "POST",
                url: import.meta.env.VITE_API_URL + "/tokens/create",
                data: { email: this.email }
            };
            axios
                .request(options)
                .then(response => {
                    console.log(response.data);
                    this.token = response.data.token;
                })
                .catch(function (error) {
                    console.error(error);
                });
        },
        logout() {
            axios
                .get(import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie")
                .then(response => {
                    let options = {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json"
                        },
                        url: import.meta.env.VITE_APP_URL + "/logout"
                    };
                    axios
                        .request(options)
                        .then(response => {
                            console.log(response.status);
                            if (response.status === 204) {
                                this.isLoggedIn = false;
                            }
                        })
                        .catch(function (error) {
                            console.error(error);
                        });
                });
        },
        enable2FA() {
            axios
                .get(import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie", {
                    withCredentials: true
                })
                .then(response => {
                    const AuthStore = useAuthStore();
                    let options = {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + AuthStore.token
                        },
                        withCredentials: true,
                        url:
                            import.meta.env.VITE_APP_URL +
                            "/user/two-factor-authentication"
                    };
                    axios.request(options).then(response => {
                        console.log("2fa: " + response.status);
                        if (response.status === 200) {
                            this.getLoggedInUserDetails();
                        }
                    });
                });
        },
        disable2FA() {
            axios
                .get(import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie", {
                    withCredentials: true
                })
                .then(response => {
                    const AuthStore = useAuthStore();
                    let options = {
                        method: "DELETE",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + AuthStore.token
                        },
                        withCredentials: true,
                        url:
                            import.meta.env.VITE_APP_URL +
                            "/user/two-factor-authentication"
                    };
                    axios.request(options).then(response => {
                        console.log("2fa: " + response.status);
                        if (response.status === 200) {
                            this.getLoggedInUserDetails();
                        }
                    });
                });
        },
        async getLoggedInUserDetails() {
            if (this.authUser.id !== 0) {
                return;
            }

            try {
                const AuthStore = useAuthStore();
                await axios.get(
                    import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie",
                    {
                        withCredentials: true
                    }
                );

                let options = {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + AuthStore.token
                    },
                    withCredentials: true,
                    url: import.meta.env.VITE_API_URL + "/get-user-details"
                };

                const response = await axios.request(options);
                this.authUser = response.data.user;
                this.email = response.data.user.email;
                this.qrSvg = response.data.twoFactorQrCodeSvg;
                this.role = response.data.role;
                this.permissions = response.data.permissions;
                this.activatedPermissionsName =
                    response.data.activatedPermissionsName;
                this.myTeam = response.data.myTeam;
                this.myClient = response.data.myClient;
                this.myClientAccountManager = response.data.myClient;
                this.myShift = response.data.myShift;
                this.lateNotification = response.data.lateNotification;
                this.clientUser = response.data.client_user;
            } catch (error) {
                if (
                    //@ts-ignore
                    error.response &&
                    //@ts-ignore
                    error.response.data.status === "config error"
                ) {
                    this.showGlobalModal = true;
                    //@ts-ignore
                    this.globalModalMessage = error.response.data.error;
                }
            }
        },
        async impersonate(id: string) {
            try {
                const AuthStore = useAuthStore();
                let options = {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + AuthStore.token
                    },
                    url:
                        import.meta.env.VITE_API_URL + "/" + id + "/impersonate"
                };
                const response = await axios.request(options);
                if (response.status === 200) {
                    console.log("impersonate called", response.data);

                    this.impersonated = response.data.impersonated;
                    this.impersonator = response.data.impersonator;
                    return response.data;
                } else {
                    throw new Error(response.data.message);
                }
            } catch (error) {
                console.log("impersonate::error");
                console.log(error);
            }
        },
        async leaveImpersonate() {
            try {
                const AuthStore = useAuthStore();
                let options = {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + AuthStore.token
                    },
                    url: import.meta.env.VITE_API_URL + "/leave-impersonate"
                };
                await axios.request(options).then(response => {
                    console.log("impersonate called");
                    this.impersonated = "";
                    this.impersonator = "";
                    // window.location.reload();
                    window.location.href = "/";
                });
            } catch (error) {
                console.log("impersonate::error");
                console.log(error);
            }
        },
        hasPermission(permission: string) {
            return this.activatedPermissionsName.includes(permission);
        },
        resetGlobalModal() {
            this.showGlobalModal = false;
            this.globalModalTitle = "";
            this.globalModalMessage = "";
        },
        async changePassword(
            currentPassword: string,
            password: string,
            confirmPassword: string,
            timezone_id: string,
            skipCurrentPassword: boolean
        ) {
            await axios.get(
                import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
            );
            await axios.put(import.meta.env.VITE_APP_URL + "/user/password", {
                current_password: currentPassword,
                password: password,
                password_confirmation: confirmPassword,
                timezone_id: timezone_id,
                skip_current_password: skipCurrentPassword
            });
        },
        async updateProfile(firstName: string, lastName: string) {
            const baseUrl = import.meta.env.VITE_API_URL;

            await new Http(baseUrl).put('/user/profile', {
                first_name: firstName,
                last_name: lastName,
            });
        },
        async skipChangePassword() {
            const baseUrl = import.meta.env.VITE_API_URL;
            await axios.get(
                import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
            );
            await axios.put(baseUrl + "/user/skip-password-change");
        },
        async forgotPassword(email: string) {
            await axios.get(
                import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
            );
            return await axios.post(
                import.meta.env.VITE_APP_URL + "/user/forgot-password",
                {
                    email: email
                }
            );
        },
        async resetPassword(
            token: string,
            email: string,
            password: string,
            password_confirmation: string
        ) {
            await axios.get(
                import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
            );
            return await axios.post(
                import.meta.env.VITE_APP_URL + "/reset-password",
                {
                    token: token,
                    email: email,
                    password: password,
                    password_confirmation: password_confirmation
                }
            );
        },
        async verifyToken(token: string, email: string) {
            await axios.get(
                import.meta.env.VITE_APP_URL + "/sanctum/csrf-cookie"
            );
            return await axios.post(
                import.meta.env.VITE_APP_URL + "/user/verify-token",
                {
                    token: token,
                    email: email
                }
            );
        },
        setTicketApproval(data: any) {
            this.canApproveTicket = data;
            localStorage.setItem(
                "canApproveTicket",
                JSON.stringify(this.canApproveTicket)
            );
        },
        loadTicketApproval() {
            const storedData = localStorage.getItem("canApproveTicket");
            if (storedData) {
                this.canApproveTicket = JSON.parse(storedData);
            }

        },
        async getLoginLinks() {
            const response = await axios.get(
                import.meta.env.VITE_APP_URL + "/api/login-links"
            );
            this.loginLinks = response.data;
        }
    }
});
